import { Injectable } from '@angular/core';
import {
  EscoSkill,
  ProfileDetail,
  ProfileUpdate,
} from '@app/profile/models/profile.models';
import { HttpService } from '@avenir-client-web/http';
import {
  ListResponse,
  PagingResponse,
  RequestOption,
  SingleResponse,
} from '@avenir-client-web/models';
import { ProfileService } from '@core/services/profile.service';
import {
  escoSkillApi,
  externalProfileApi,
  profileApi,
  relatedPeopleApi,
} from '@shared/constants/api.constants';
import { map, Observable, pluck, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PersonalProfileService {
  hasMadeChange$ = new Subject<boolean>();

  personalInfo: ProfileDetail;

  relatedPeople: ProfileDetail[];

  constructor(
    private readonly httpService: HttpService,
    private readonly profileService: ProfileService
  ) {}

  update(profileDetail: ProfileDetail): Observable<ProfileDetail> {
    const body: ProfileUpdate[] = [
      {
        op: 'replace',
        path: 'description',
        value: profileDetail.description,
      },
      {
        op: 'replace',
        path: 'skills',
        value: profileDetail.skills,
      },
      {
        op: 'replace',
        path: 'preferredLanguage',
        value: profileDetail.preferredLanguage,
      },
    ];

    return this.httpService
      .patch<SingleResponse<ProfileDetail>>(
        `${profileApi}/${profileDetail.externalId}`,
        body
      )
      .pipe(pluck('data'));
  }

  getUserProfile(externalId: string): Observable<ProfileDetail> {
    return this.httpService
      .get<SingleResponse<ProfileDetail>>(`${profileApi}/${externalId}`)
      .pipe(pluck('data'));
  }

  getExternalUserProfile(id: string): Observable<ProfileDetail> {
    return this.httpService
      .get<SingleResponse<ProfileDetail>>(
        `${profileApi}/${externalProfileApi}/${id}`
      )
      .pipe(pluck('data'));
  }

  getRelatedPeople(externalId: string): Observable<ProfileDetail[]> {
    return this.httpService
      .get<ListResponse<ProfileDetail>>(
        `${profileApi}/${externalId}/${relatedPeopleApi}`
      )
      .pipe(
        pluck('data'),
        map(relatedPeople =>
          this.profileService.mapResponsiblePersons(relatedPeople)
        )
      );
  }

  getExternalRelatedPeople(id: string): Observable<ProfileDetail[]> {
    return this.httpService
      .get<ListResponse<ProfileDetail>>(
        `${profileApi}/${externalProfileApi}/${id}/${relatedPeopleApi}`
      )
      .pipe(
        pluck('data'),
        map(relatedPeople =>
          this.profileService.mapResponsiblePersons(relatedPeople)
        )
      );
  }

  getSkills(
    requestOption: RequestOption
  ): Observable<PagingResponse<EscoSkill>> {
    const url = this.httpService.buildRequestUrl(escoSkillApi, requestOption);

    return this.httpService.get<PagingResponse<EscoSkill>>(url);
  }
}
